import { Injectable, NgZone } from "@angular/core";
import { ChangeState } from "@docma-mm-core/model/change-state/change-state";
import { LoadState } from "@docma-mm-core/model/load-state/load-state";
import { SyncState } from "@docma-mm-core/model/sync-state/sync-state";
import { MmDbService } from "../../mm-db/mm-db-service";
import { MmProjectDbCollections } from "../../mm-db/mm-project-db-schema";
import { GroupData } from "./group";

@Injectable()
export class GroupDbService {

    constructor(private readonly mmDbService: MmDbService,
                private ngZone: NgZone) {

    }

    async saveChangeState(mydocmaUid: string, groupUid: string, changeState: ChangeState<GroupData>, throwErrorIfNotFound?: boolean): Promise<ChangeState<GroupData>> {
      return await this.ngZone.runOutsideAngular(async () => {
        const db = await this.mmDbService.getProjectDb(mydocmaUid);
        const groupEntity = await db.get(MmProjectDbCollections.groups as never, groupUid);
        if (groupEntity) {
          try {
            groupEntity.changeState = changeState;
            await db.set(MmProjectDbCollections.groups as never, groupEntity);

          } catch (err) {
            // TODO: Log error
            if (throwErrorIfNotFound) {
              throw new Error(`An error while persisting group change state in project ${mydocmaUid}.` + err.message);
            } else {
              console.log(`An error while persisting group change state in project ${mydocmaUid}.` + err.message);
            }
            return null;
          }
        } else {
          console.warn(`Group uid ${groupUid} not found in project store ${mydocmaUid}`);
        }

        await db.set(MmProjectDbCollections.groups as never, groupEntity);
        return new ChangeState<GroupData>(changeState);
      });
  }


  async saveLoadState(mydocmaUid: string, groupUid: string, loadState: LoadState<GroupData>, throwErrorIfNotFound?: boolean): Promise<LoadState<GroupData>> {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const groupEntity = await db.get(MmProjectDbCollections.groups as never, groupUid);
      if (groupEntity) {
        try {
          groupEntity.loadState = loadState;
          groupEntity.id = loadState.data?.Id ?? groupEntity.id;

          await db.set(MmProjectDbCollections.groups as never, groupEntity);

        } catch (err) {
          // TODO: Log error
          if (throwErrorIfNotFound) {
            throw new Error(`An error while persisting group load state in project ${mydocmaUid}.` + err.message);
          } else {
            console.log(`An error while persisting group load state in project ${mydocmaUid}.` + err.message);
          }
          return null;
        }
      } else {
        throw new Error(`Group uid ${groupUid} not found in project store ${mydocmaUid}`);
      }

      await db.set(MmProjectDbCollections.groups as never, groupEntity);
      return new LoadState<GroupData>(loadState);
    });
  }

  async saveSyncState(mydocmaUid: string, groupUid: string, state: SyncState, throwErrorIfNotFound?: boolean) {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const groupEntity = await db.get(MmProjectDbCollections.groups as never, groupUid);
      if (groupEntity) {
        try {
          groupEntity.syncState = {...state};
          delete groupEntity.syncState.changes;
          await db.set(MmProjectDbCollections.groups as never, groupEntity);

        } catch (err) {
          // TODO: Log error
          if (throwErrorIfNotFound) {
            throw new Error(`An error while persisting group sync state in project ${mydocmaUid}.` + err.message);
          } else {
            console.log(`An error while persisting group sync state in project ${mydocmaUid}.` + err.message);
          }
        }
      } else {
        console.warn(`Group uid ${groupUid} not found in project store ${mydocmaUid}`);
      }
      return state;
    });
  }

  async getLoadState(mydocmaUid: string, groupUid: string, throwErrorIfNotFound?: boolean): Promise<LoadState<GroupData>> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const groupEntity = await db.get(MmProjectDbCollections.groups as never, groupUid);
    if (groupEntity) {
      return groupEntity.loadState;
    }
    return null;
  }
}
